import { useCallback, useRef } from 'react';
import { escapeRegExp } from '@jpmuitk/utils';
import { itemToString as defaultItemToString } from './itemToString';
var TYPE_SELECT_TIMEOUT = 1500;
export function useTypeSelect(options) {
  var getItemAtIndex = options.getItemAtIndex,
      highlightedIndex = options.highlightedIndex,
      itemCount = options.itemCount,
      _options$itemToString = options.itemToString,
      itemToString = _options$itemToString === void 0 ? defaultItemToString : _options$itemToString,
      onTypeSelect = options.onTypeSelect,
      setFocusVisible = options.setFocusVisible,
      setHighlightedIndex = options.setHighlightedIndex;
  var getItemIndexForSearch = useCallback(function (searchTerm, fromIndex) {
    if (itemCount === 0) {
      return null;
    }

    var index = fromIndex || 0;

    while (index < itemCount) {
      var item = getItemAtIndex(index);
      var textValue = itemToString ? itemToString(item) : item;

      if (textValue && textValue.match(new RegExp("^".concat(escapeRegExp(searchTerm)), 'i'))) {
        return index;
      }

      index = index + 1;
    }

    return null;
  }, [itemCount, itemToString, getItemAtIndex]);
  var state = useRef({
    search: '',
    timeout: null
  }).current; // eslint-disable-next-line complexity

  var onKeyDownCapture = function onKeyDownCapture(event) {
    var character = getStringForKey(event.key);

    if (!character || event.ctrlKey || event.metaKey) {
      return;
    } // Do not propagate the Spacebar event if it's meant to be part of the search.
    // When we time out, the search term becomes empty, hence the check on length.
    // Trimming is to account for the case of pressing the Spacebar more than once,
    // which should cycle through the selection/deselection of the focused item.


    if (character !== ' ' || state.search.trim().length > 0) {
      event.preventDefault();
      event.stopPropagation();
    } // When typing same character sebsequently and *quickly*, we treat it as cyling through items
    // starting with that char instead of seaching for double character, because in most cases
    // there won't be any option having same characters at the beginning. This should be only
    // impacting beginning of the words because length of `character` would be 1.


    if (character !== state.search) {
      state.search += character;
    } // Prioritize items after the currently focused item, falling back to searching the whole list.
    // We want to cycle through choices when keep typing the same first character, hence the +1
    // condition. All subsequent characters should stay at the current item otherwise it will
    // always jumping around.


    var index = getItemIndexForSearch(state.search, state.search.length > 1 ? highlightedIndex : highlightedIndex + 1); // If no key found, search from the top.

    if (index == null) {
      index = getItemIndexForSearch(state.search);
    }

    if (index != null) {
      setFocusVisible(true);
      setHighlightedIndex(index);

      if (onTypeSelect) {
        onTypeSelect(event, index);
      }
    }

    clearTimeout(state.timeout);
    state.timeout = setTimeout(function () {
      state.search = '';
    }, TYPE_SELECT_TIMEOUT);
  }; // Use case: e.g. dropdown may want to clear the timeout after


  var clearTypeSelectBuffer = function clearTypeSelectBuffer() {
    state.search = '';

    if (state.timeout) {
      clearTimeout(state.timeout);
    }
  };

  return {
    clearTypeSelectBuffer: clearTypeSelectBuffer,
    // Using a capturing listener to catch the keydown event before
    // other hooks in order to handle the Spacebar event.
    onKeyDownCapture: onKeyDownCapture
  };
}

function getStringForKey(key) {
  // If the key is of length 1, it is an ASCII value.
  // Otherwise, if there are no ASCII characters in the key name,
  // it is a Unicode character.
  // See https://www.w3.org/TR/uievents-key/
  if (key.length === 1 || !/^[A-Z]/i.test(key)) {
    return key;
  }

  return '';
}